import { Routes, Route } from 'react-router';
import { Suspense, useEffect, useState } from 'react';
import { ROUTER } from './constants/router';
import { AuthProvider, PermissionGuard, PrivateRoutes } from '@xinchuantw/auth-react';
import './index.css';
import Dashboard from './pages/Dashboard';
import Fulfillment from './pages/Fulfillment';
import Aggregator from './pages/Aggregator';
import Wallet from './pages/Wallet';
import Report from './pages/Report';
import ProductGroup from './pages/ProductGroup';
import ProductManagement from './pages/ProductManagement';
import VoucherPinImport from './pages/VoucherPinImport';
import Layout from './components/layout/layout';
import { Spin } from 'antd';
import PERMISSIONS_FULFILLMENT_PAGE from './constants/permissions/fulfillmentPage';
import PERMISSIONS_AGGREGATOR_PAGE from './constants/permissions/aggregatorPage';
import PERMISSIONS_WALLET_PAGE from './constants/permissions/walletPage';
import PERMISSIONS_REPORT_PAGE from './constants/permissions/reportPage';
import PERMISSIONS_PRODUCT_GROUP_PAGE from './constants/permissions/productGroupPage';
import PERMISSIONS_PRODUCT_MANAGEMENT_PAGE from './constants/permissions/productManagementPage';
import PERMISSIONS_VOUCHER_PIN_IMPORT_PAGE from './constants/permissions/voucherPinImportPage';
import NoPermission from './pages/NoPermission';

/** Full-screen loader for app boot (no layout yet): AuthProvider + top-level Suspense. */
const LoadingFallback = () => {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Spin size="large" />
    </div>
  );
};

/** Show the per-page permission loader only if the check takes longer than this. */
const PAGE_LOADING_DELAY_MS = 300;

/**
 * Contained loader for per-page permission checks. Renders inside the Layout
 * content area (below the header) so it doesn't overflow the viewport and blends
 * into each page's own table/data spinner.
 *
 * The spinner is delayed by {@link PAGE_LOADING_DELAY_MS}: when a permission check
 * resolves faster than that, PermissionGuard unmounts this fallback before the
 * timer fires, so no spinner flashes.
 */
const PageLoadingFallback = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), PAGE_LOADING_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center">
      <Spin size="large" />
    </div>
  );
};

const permissionGuardProps = {
  loadingFallback: <PageLoadingFallback />,
  fallback: <NoPermission />,
};

function App() {
  return (
    <AuthProvider loadingFallback={<LoadingFallback />}>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<PrivateRoutes />}>
            <Route element={<Layout />}>
              {/* No PermissionGuard — the dashboard is visible to every authenticated role. */}
              <Route path={ROUTER.DASHBOARD()} element={<Dashboard />} />
              <Route
                path={ROUTER.FULFILLMENT()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_FULFILLMENT_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <Fulfillment />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.AGGREGATOR()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_AGGREGATOR_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <Aggregator />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.WALLET()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_WALLET_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <Wallet />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.REPORT()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_REPORT_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <Report />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.PRODUCT_GROUP()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_PRODUCT_GROUP_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <ProductGroup />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.PRODUCT_MANAGEMENT()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_PRODUCT_MANAGEMENT_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <ProductManagement />
                  </PermissionGuard>
                }
              />
              <Route
                path={ROUTER.VOUCHER_PIN_IMPORT()}
                element={
                  <PermissionGuard
                    requiredPermissions={[PERMISSIONS_VOUCHER_PIN_IMPORT_PAGE.VIEW]}
                    {...permissionGuardProps}
                  >
                    <VoucherPinImport />
                  </PermissionGuard>
                }
              />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
